import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, MountainSnow, Star } from "lucide-react";

import PackageCard from "@/components/PackageCard";
import useReveal from "@/hooks/useReveal";
import { fetchPackages } from "@/lib/api";
import {
  HIGHLIGHTS,
  TESTIMONIALS,
  WHY_CHOOSE,
  buildWaLink,
} from "@/lib/constants";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1718679388215-75b00db812f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/18005881/18005881-hd_1920_1080_30fps.mp4";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useReveal([featured.length]);

  useEffect(() => {
    fetchPackages({ featured: true })
      .then(setFeatured)
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] overflow-hidden grain"
      >
        <div className="absolute inset-0">
          <video
            data-testid="hero-video"
            src={HERO_VIDEO}
            poster={HERO_IMAGE}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#11233B]/65 via-[#1F3A5F]/40 to-[#11233B]/85" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-56 pb-24 md:pb-32 text-[#FAF4ED]">
          <p className="eyebrow text-[#E8B89B] mb-6">Ladakh · Himalayan India</p>
          <h1
            data-testid="hero-heading"
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl"
          >
            Discover the magic of <em className="not-italic text-[#E8B89B]">Ladakh.</em>
            <br />
            Curated experiences. Real adventures.
          </h1>
          <p className="mt-8 text-base sm:text-lg max-w-2xl text-[#FAF4ED]/85 leading-relaxed">
            We don't sell trips off a shelf. iExplore designs Ladakh journeys
            from years of on-ground experience — across high passes, hidden
            valleys, and the silent monasteries of the Himalayas.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <Link
              to="/packages"
              data-testid="hero-view-packages"
              className="inline-flex items-center justify-center gap-2 bg-[#E8B89B] hover:bg-[#DDA98B] text-[#1F3A5F] px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-colors"
            >
              View Packages <ArrowUpRight size={16} strokeWidth={1.6} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-plan-trip"
              className="inline-flex items-center justify-center gap-2 border border-[#FAF4ED]/40 hover:border-[#FAF4ED] text-[#FAF4ED] px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Plan My Trip
            </Link>
          </div>

          <div className="mt-20 hidden md:flex items-center gap-3 text-[#FAF4ED]/70 text-xs uppercase tracking-[0.22em]">
            <ArrowDown size={14} className="animate-bounce" />
            <span>Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section
        data-testid="why-section"
        className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 reveal">
          <div className="md:col-span-5">
            <p className="eyebrow mb-5">Why Travellers Choose Us</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1F3A5F] leading-[1.1]">
              Built by travel veterans.<br />
              <em className="not-italic text-[#1F3A5F]/60">Refined by the mountains.</em>
            </h2>
            <p className="mt-7 text-[#5A6B82] leading-relaxed max-w-md text-base">
              iExplore wasn't built in a boardroom. It's a decade of running
              Ladakh trips — distilled into journeys that respect the altitude,
              the culture, and your time.
            </p>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1F3A5F]/10">
            {WHY_CHOOSE.map((w, i) => (
              <div
                key={w.title}
                data-testid={`why-card-${i}`}
                className="bg-[#FAF4ED] p-8 md:p-10 hover:bg-white transition-colors duration-500"
              >
                <span className="text-[#E8B89B] font-serif text-2xl">
                  0{i + 1}
                </span>
                <h3 className="font-serif text-2xl text-[#1F3A5F] mt-4">
                  {w.title}
                </h3>
                <p className="text-sm text-[#5A6B82] mt-3 leading-relaxed">
                  {w.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PACKAGES */}
      <section
        data-testid="featured-section"
        className="bg-white border-y border-[#1F3A5F]/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 reveal">
            <div>
              <p className="eyebrow mb-4">Featured Journeys</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1F3A5F] leading-[1.1]">
                Four Ladakh stories,<br />curated for modern travellers.
              </h2>
            </div>
            <Link
              to="/packages"
              data-testid="featured-view-all"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1F3A5F] border-b border-[#1F3A5F] pb-1 self-start md:self-end"
            >
              View All Packages <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8 reveal">
            {featured.map((p) => (
              <PackageCard key={p.slug} pkg={p} variant="compact" />
            ))}
            {featured.length === 0 &&
              [0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-[4/5] bg-[#1F3A5F]/5 animate-pulse"
                />
              ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS BENTO */}
      <section
        data-testid="highlights-section"
        className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32"
      >
        <div className="text-center reveal max-w-2xl mx-auto">
          <p className="eyebrow mb-4">Ladakh Highlights</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1F3A5F] leading-[1.1]">
            Four landscapes that change you.
          </h2>
          <p className="mt-6 text-[#5A6B82] leading-relaxed">
            From the world's highest motorable pass to a saltwater lake at
            14,000 feet — every iExplore journey threads through these icons.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5 reveal">
          {HIGHLIGHTS.map((h, idx) => {
            // Bento span pattern: 7-5, 5-7
            const span =
              idx === 0
                ? "md:col-span-7"
                : idx === 1
                  ? "md:col-span-5"
                  : idx === 2
                    ? "md:col-span-5"
                    : "md:col-span-7";
            return (
              <div
                key={h.name}
                data-testid={`highlight-${idx}`}
                className={`group relative ${span} aspect-[4/3] md:aspect-auto md:h-[440px] overflow-hidden bg-[#11233B]`}
              >
                <img
                  src={h.image}
                  alt={h.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover zoom-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A5F]/90 via-[#1F3A5F]/30 to-transparent" />
                <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end text-[#FAF4ED]">
                  <div className="flex items-center gap-2 mb-3">
                    <MountainSnow size={14} strokeWidth={1.4} className="text-[#E8B89B]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#E8B89B]">
                      {h.altitude}
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl">{h.name}</h3>
                  <p className="mt-3 text-sm md:text-base text-[#FAF4ED]/85 max-w-md leading-relaxed">
                    {h.blurb}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="bg-[#11233B] text-[#FAF4ED]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="reveal max-w-2xl">
            <p className="eyebrow mb-4 text-[#E8B89B]">Travellers' Voices</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.1]">
              Stories from the road less travelled.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 reveal">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="border-t border-[#FAF4ED]/15 pt-8"
              >
                <div className="flex items-center gap-1 mb-5 text-[#E8B89B]">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="font-serif text-xl md:text-2xl leading-snug text-[#FAF4ED]/95">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 text-xs uppercase tracking-[0.2em] text-[#FAF4ED]/60">
                  {t.name} · {t.location}
                  <span className="block mt-1 text-[#E8B89B] not-italic">
                    {t.trip}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="cta-section"
        className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 reveal"
      >
        <div className="bg-white border border-[#1F3A5F]/10 p-10 md:p-20 text-center">
          <p className="eyebrow mb-4">Ready When You Are</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl text-[#1F3A5F] leading-[1.05] max-w-3xl mx-auto">
            Chat with us on WhatsApp and start planning your Ladakh trip — today.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 justify-center">
            <a
              href={buildWaLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bf5a] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Chat on WhatsApp
            </a>
            <Link
              to="/contact"
              data-testid="cta-form"
              className="inline-flex items-center justify-center gap-2 bg-[#1F3A5F] hover:bg-[#11233B] text-[#FAF4ED] px-10 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Send an Inquiry
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
